import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Platform,
  FlatList,
  KeyboardAvoidingView,
  Share,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function QuotationScreen({ navigation, route }) {
  // Route params
  const { client, mode = 'list' } = route.params || {};

  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'draft', 'sent', 'accepted', 'expired'
  
  // Quotation data
  const [quotations, setQuotations] = useState([]);
  const [draftQuotations, setDraftQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // UI state
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [showQuotationDetailsModal, setShowQuotationDetailsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Quotation form state
  const [quotationForm, setQuotationForm] = useState({
    clientId: client?.id || '',
    clientName: client?.name || '',
    clientEmail: client?.email || '',
    clientPhone: client?.phone || '',
    clientAddress: client?.address || '',
    quotationNumber: '',
    title: '',
    description: '',
    validUntil: '',
    items: [],
    subtotal: 0,
    taxRate: 20, // 20% VAT
    taxAmount: 0,
    discountType: 'percentage', // 'percentage' or 'fixed'
    discountValue: 0,
    discountAmount: 0,
    totalAmount: 0,
    currency: 'GBP',
    paymentTerms: '30 days',
    notes: '',
    termsAndConditions: '',
    companyInfo: {
      name: 'Your Company Name',
      address: 'Your Company Address',
      phone: 'Your Phone Number',
      email: 'your@email.com',
      website: 'www.yourwebsite.com',
      logo: '',
    },
  });

  // Initialize component
  useEffect(() => {
    initializeQuotationScreen();
  }, []);

  // Handle mode changes
  useEffect(() => {
    if (mode === 'create' && client) {
      setQuotationForm(prev => ({
        ...prev,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email || '',
        clientPhone: client.phone || '',
        clientAddress: client.address || '',
      }));
      setShowQuotationModal(true);
    }
  }, [mode, client]);

  // Initialize quotation screen data
  const initializeQuotationScreen = useCallback(async () => {
    try {
      setIsLoading(true);

      // Load quotations
      await loadQuotations();
      
      // Load draft quotations
      await loadDraftQuotations();
      
      // Load clients
      await loadClients();
      
      // Load templates
      await loadTemplates();

    } catch (error) {
      console.error('Initialize quotation screen error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load quotations
  const loadQuotations = useCallback(async () => {
    try {
      // Try API first, fall back to cache
      let quotationsData = null;
      try {
        const resp = await apiRequest('quotations?status=sent');
        if (resp.success && resp.data) {
          quotationsData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('sent_quotations', quotationsData);
        }
      } catch (apiErr) {
        quotationsData = await AsyncStorage.getItem('sent_quotations');
      }
      if (quotationsData) {
        const parsed = JSON.parse(quotationsData);
        // Sort by creation date (newest first)
        parsed.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setQuotations(parsed);
      } else {
        // Initialize with sample data
        const sampleQuotations = [
          {
            id: '1',
            quotationNumber: 'QUO-2024-001',
            clientId: '1',
            clientName: 'ABC Construction Ltd',
            clientEmail: 'contact@abcconstruction.com',
            title: 'Office Renovation Project',
            description: 'Complete office renovation including electrical, plumbing, and interior design',
            status: 'sent',
            createdAt: '2024-11-10T10:00:00Z',
            sentAt: '2024-11-10T14:30:00Z',
            validUntil: '2024-12-10T23:59:59Z',
            items: [
              { id: '1', description: 'Electrical Installation', quantity: 1, unit: 'project', unitPrice: 15000, total: 15000 },
              { id: '2', description: 'Plumbing Work', quantity: 1, unit: 'project', unitPrice: 8000, total: 8000 },
              { id: '3', description: 'Interior Design & Finishing', quantity: 1, unit: 'project', unitPrice: 12000, total: 12000 },
            ],
            subtotal: 35000,
            taxRate: 20,
            taxAmount: 7000,
            discountType: 'percentage',
            discountValue: 5,
            discountAmount: 1750,
            totalAmount: 40250,
            currency: 'GBP',
            paymentTerms: '30 days',
            notes: 'All materials included. Work to be completed within 8 weeks.',
            termsAndConditions: 'Standard terms and conditions apply.',
          },
          {
            id: '2',
            quotationNumber: 'QUO-2024-002',
            clientId: '2',
            clientName: 'Green Energy Solutions',
            clientEmail: 'info@greenenergy.com',
            title: 'Solar Panel Installation',
            description: 'Installation of solar panels for residential property',
            status: 'accepted',
            createdAt: '2024-11-08T09:15:00Z',
            sentAt: '2024-11-08T11:20:00Z',
            acceptedAt: '2024-11-09T16:45:00Z',
            validUntil: '2024-12-08T23:59:59Z',
            items: [
              { id: '1', description: 'Solar Panels (20 units)', quantity: 20, unit: 'pcs', unitPrice: 300, total: 6000 },
              { id: '2', description: 'Installation & Setup', quantity: 1, unit: 'project', unitPrice: 2500, total: 2500 },
              { id: '3', description: 'Electrical Connection', quantity: 1, unit: 'project', unitPrice: 1500, total: 1500 },
            ],
            subtotal: 10000,
            taxRate: 20,
            taxAmount: 2000,
            discountType: 'fixed',
            discountValue: 500,
            discountAmount: 500,
            totalAmount: 11500,
            currency: 'GBP',
            paymentTerms: '50% advance, 50% on completion',
            notes: '25-year warranty on solar panels. Installation within 2 weeks.',
          },
        ];
        setQuotations(sampleQuotations);
        await AsyncStorage.setItem('sent_quotations', JSON.stringify(sampleQuotations));
        try { await apiRequest('quotations', { method: 'POST', body: { quotations: sampleQuotations } }); } catch(e) {}
      }
    } catch (error) {
      console.error('Load quotations error:', error);
    }
  }, []);

  // Load draft quotations
  const loadDraftQuotations = useCallback(async () => {
    try {
      // Try API first for drafts
      let draftData = null;
      try {
        const resp = await apiRequest('quotations?status=draft');
        if (resp.success && resp.data) {
          draftData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('draft_quotations', draftData);
        }
      } catch (apiErr) {
        draftData = await AsyncStorage.getItem('draft_quotations');
      }
      setDraftQuotations(draftData ? JSON.parse(draftData) : []);
    } catch (error) {
      console.error('Load draft quotations error:', error);
    }
  }, []);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      // Try API first for clients
      let clientsData = null;
      try {
        const resp = await apiRequest('quotation-clients');
        if (resp.success && resp.data) {
          clientsData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('quotation_clients', clientsData);
        }
      } catch (apiErr) {
        clientsData = await AsyncStorage.getItem('quotation_clients');
      }
      if (clientsData) {
        setClients(JSON.parse(clientsData));
      } else {
        // Initialize with sample clients
        const sampleClients = [
          { id: '1', name: 'ABC Construction Ltd', email: 'contact@abcconstruction.com', phone: '+44 20 1234 5678' },
          { id: '2', name: 'Green Energy Solutions', email: 'info@greenenergy.com', phone: '+44 161 987 6543' },
          { id: '3', name: 'Modern Interiors', email: 'hello@moderninteriors.co.uk', phone: '+44 121 456 7890' },
        ];
        setClients(sampleClients);
        await AsyncStorage.setItem('quotation_clients', JSON.stringify(sampleClients));
        try { await apiRequest('quotation-clients', { method: 'POST', body: { clients: sampleClients } }); } catch(e) {}
      }
    } catch (error) {
      console.error('Load clients error:', error);
    }
  }, []);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      // Try API first for templates
      let templatesData = null;
      try {
        const resp = await apiRequest('quotation-templates');
        if (resp.success && resp.data) {
          templatesData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('quotation_templates', templatesData);
        }
      } catch (apiErr) {
        templatesData = await AsyncStorage.getItem('quotation_templates');
      }
      if (templatesData) {
        setTemplates(JSON.parse(templatesData));
      } else {
        // Initialize with sample templates
        const sampleTemplates = [
          {
            id: '1',
            name: 'Construction Project',
            items: [
              { description: 'Site Preparation', quantity: 1, unit: 'project', unitPrice: 5000 },
              { description: 'Foundation Work', quantity: 1, unit: 'project', unitPrice: 15000 },
              { description: 'Structural Work', quantity: 1, unit: 'project', unitPrice: 25000 },
              { description: 'Finishing Work', quantity: 1, unit: 'project', unitPrice: 10000 },
            ],
            paymentTerms: '30% advance, 40% on structure completion, 30% on handover',
            notes: 'All materials included. Timeline subject to weather conditions.',
          },
          {
            id: '2',
            name: 'Electrical Installation',
            items: [
              { description: 'Wiring Installation', quantity: 1, unit: 'project', unitPrice: 8000 },
              { description: 'Switch & Socket Installation', quantity: 50, unit: 'pcs', unitPrice: 25 },
              { description: 'Light Fixture Installation', quantity: 20, unit: 'pcs', unitPrice: 50 },
              { description: 'Electrical Panel Setup', quantity: 1, unit: 'project', unitPrice: 2000 },
            ],
            paymentTerms: '50% advance, 50% on completion',
            notes: 'All electrical work certified. 2-year warranty included.',
          },
        ];
        setTemplates(sampleTemplates);
        await AsyncStorage.setItem('quotation_templates', JSON.stringify(sampleTemplates));
        try { await apiRequest('quotation-templates', { method: 'POST', body: { templates: sampleTemplates } }); } catch(e) {}
      }
    } catch (error) {
      console.error('Load templates error:', error);
    }
  }, []);

  // Generate quotation number
  const generateQuotationNumber = useCallback(() => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const day = String(new Date().getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `QUO-${year}${month}${day}-${random}`;
  }, []);

  // Calculate totals
  const calculateTotals = useCallback((items, taxRate, discountType, discountValue) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = (subtotal * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }
    
    const discountedSubtotal = subtotal - discountAmount;
    const taxAmount = (discountedSubtotal * taxRate) / 100;
    const totalAmount = discountedSubtotal + taxAmount;
    
    return {
      subtotal,
      discountAmount,
      taxAmount,
      totalAmount,
    };
  }, []);

  // Update item calculations
  const updateItemCalculations = useCallback(() => {
    const updatedItems = quotationForm.items.map(item => ({
      ...item,
      total: (item.quantity || 0) * (item.unitPrice || 0),
    }));
    
    const totals = calculateTotals(
      updatedItems,
      quotationForm.taxRate,
      quotationForm.discountType,
      quotationForm.discountValue
    );
    
    setQuotationForm(prev => ({
      ...prev,
      items: updatedItems,
      ...totals,
    }));
  }, [quotationForm.items, quotationForm.taxRate, quotationForm.discountType, quotationForm.discountValue, calculateTotals]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await initializeQuotationScreen();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  }, [initializeQuotationScreen]);

  // Save draft quotation
  const saveDraftQuotation = useCallback(async () => {
    try {
      if (!quotationForm.title.trim()) {
        Alert.alert('Error', 'Please enter quotation title');
        return;
      }

      if (!quotationForm.clientName.trim()) {
        Alert.alert('Error', 'Please enter client name');
        return;
      }

      const draftQuotation = {
        id: selectedQuotation?.id || Date.now().toString(),
        ...quotationForm,
        quotationNumber: quotationForm.quotationNumber || generateQuotationNumber(),
        lastModified: new Date().toISOString(),
        status: 'draft',
      };

      let updatedDrafts;
      if (selectedQuotation && selectedQuotation.status === 'draft') {
        // Update existing draft
        updatedDrafts = draftQuotations.map(quotation => 
          quotation.id === selectedQuotation.id ? draftQuotation : quotation
        );
      } else {
        // Add new draft
        updatedDrafts = [...draftQuotations, draftQuotation];
      }

      setDraftQuotations(updatedDrafts);
      await AsyncStorage.setItem('draft_quotations', JSON.stringify(updatedDrafts));

      setShowQuotationModal(false);
      Alert.alert('Success', 'Draft saved successfully!');

    } catch (error) {
      console.error('Save draft quotation error:', error);
      Alert.alert('Error', 'Failed to save draft. Please try again.');
    }
  }, [quotationForm, selectedQuotation, draftQuotations, generateQuotationNumber]);

  // Send quotation
  const sendQuotation = useCallback(async () => {
    try {
      // Validate required fields
      if (!quotationForm.title.trim()) {
        Alert.alert('Error', 'Please enter quotation title');
        return;
      }

      if (!quotationForm.clientName.trim()) {
        Alert.alert('Error', 'Please enter client name');
        return;
      }

      if (!quotationForm.clientEmail.trim()) {
        Alert.alert('Error', 'Please enter client email');
        return;
      }

      if (quotationForm.items.length === 0) {
        Alert.alert('Error', 'Please add at least one item');
        return;
      }

      if (!quotationForm.validUntil) {
        Alert.alert('Error', 'Please set quotation validity date');
        return;
      }

      setIsSubmitting(true);

      const sentQuotation = {
        id: selectedQuotation?.id || Date.now().toString(),
        ...quotationForm,
        quotationNumber: quotationForm.quotationNumber || generateQuotationNumber(),
        createdAt: selectedQuotation?.createdAt || new Date().toISOString(),
        sentAt: new Date().toISOString(),
        status: 'sent',
      };

      // Add to sent quotations
      const updatedQuotations = [...quotations, sentQuotation];
      setQuotations(updatedQuotations);
      try { await apiRequest('quotations', { method: 'PUT', body: { quotations: updatedQuotations } }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('sent_quotations', JSON.stringify(updatedQuotations));

      // Remove from drafts if it was a draft
      if (selectedQuotation && selectedQuotation.status === 'draft') {
        const updatedDrafts = draftQuotations.filter(quotation => quotation.id !== selectedQuotation.id);
        setDraftQuotations(updatedDrafts);
        await AsyncStorage.setItem('draft_quotations', JSON.stringify(updatedDrafts));
      }

      // Sync to server
      await syncQuotationToServer(sentQuotation);

      setShowQuotationModal(false);
      setActiveTab('sent');
      Alert.alert('Success', 'Quotation sent successfully!');

    } catch (error) {
      console.error('Send quotation error:', error);
      Alert.alert('Error', 'Failed to send quotation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [quotationForm, selectedQuotation, quotations, draftQuotations, generateQuotationNumber]);

  // Sync quotation to server
  const syncQuotationToServer = useCallback(async (quotation) => {
    try {
      // This would sync to your backend server
      console.log('Syncing quotation to server:', quotation);
    } catch (error) {
      console.error('Sync quotation to server error:', error);
    }
  }, []);

  // Add item
  const addItem = useCallback(() => {
    const newItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit: 'pcs',
      unitPrice: 0,
      total: 0,
    };
    
    setQuotationForm(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  }, []);

  // Update item
  const updateItem = useCallback((itemId, field, value) => {
    setQuotationForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      ),
    }));
  }, []);

  // Remove item
  const removeItem = useCallback((itemId) => {
    setQuotationForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId),
    }));
  }, []);

  // Apply template
  const applyTemplate = useCallback((template) => {
    const templateItems = template.items.map(item => ({
      id: Date.now().toString() + Math.random(),
      ...item,
      total: item.quantity * item.unitPrice,
    }));
    
    setQuotationForm(prev => ({
      ...prev,
      items: templateItems,
      paymentTerms: template.paymentTerms || prev.paymentTerms,
      notes: template.notes || prev.notes,
    }));
    
    setShowTemplateModal(false);
  }, []);

  // Generate PDF
  const generatePDF = useCallback(async (quotation) => {
    try {
      setIsGeneratingPDF(true);

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Quotation ${quotation.quotationNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; border-bottom: 2px solid #4A90E2; padding-bottom: 20px; }
            .company-info { flex: 1; }
            .company-name { font-size: 24px; font-weight: bold; color: #4A90E2; margin-bottom: 10px; }
            .quotation-info { text-align: right; }
            .quotation-number { font-size: 20px; font-weight: bold; color: #4A90E2; }
            .client-info { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; color: #4A90E2; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            .items-table th, .items-table td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
            .items-table th { background: #4A90E2; color: white; }
            .items-table .number { text-align: right; }
            .totals { margin-left: auto; width: 300px; }
            .total-row { display: flex; justify-content: space-between; padding: 8px 0; }
            .total-row.final { font-weight: bold; font-size: 18px; border-top: 2px solid #4A90E2; padding-top: 12px; }
            .terms { margin-top: 30px; }
            .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
              <div class="company-name">${quotation.companyInfo?.name || 'Your Company'}</div>
              <div>${quotation.companyInfo?.address || 'Your Address'}</div>
              <div>${quotation.companyInfo?.phone || 'Your Phone'}</div>
              <div>${quotation.companyInfo?.email || 'Your Email'}</div>
            </div>
            <div class="quotation-info">
              <div class="quotation-number">QUOTATION</div>
              <div>${quotation.quotationNumber}</div>
              <div>Date: ${new Date(quotation.createdAt).toLocaleDateString()}</div>
              <div>Valid Until: ${new Date(quotation.validUntil).toLocaleDateString()}</div>
            </div>
          </div>

          <div class="client-info">
            <div class="section-title">Bill To:</div>
            <div><strong>${quotation.clientName}</strong></div>
            ${quotation.clientAddress ? `<div>${quotation.clientAddress}</div>` : ''}
            ${quotation.clientEmail ? `<div>${quotation.clientEmail}</div>` : ''}
            ${quotation.clientPhone ? `<div>${quotation.clientPhone}</div>` : ''}
          </div>

          <div class="section-title">${quotation.title}</div>
          ${quotation.description ? `<p>${quotation.description}</p>` : ''}

          <table class="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit</th>
                <th class="number">Unit Price</th>
                <th class="number">Total</th>
              </tr>
            </thead>
            <tbody>
              ${quotation.items.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td>${item.quantity}</td>
                  <td>${item.unit}</td>
                  <td class="number">£${item.unitPrice.toFixed(2)}</td>
                  <td class="number">£${item.total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>£${quotation.subtotal.toFixed(2)}</span>
            </div>
            ${quotation.discountAmount > 0 ? `
              <div class="total-row">
                <span>Discount (${quotation.discountType === 'percentage' ? quotation.discountValue + '%' : '£' + quotation.discountValue}):</span>
                <span>-£${quotation.discountAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="total-row">
              <span>VAT (${quotation.taxRate}%):</span>
              <span>£${quotation.taxAmount.toFixed(2)}</span>
            </div>
            <div class="total-row final">
              <span>Total:</span>
              <span>£${quotation.totalAmount.toFixed(2)}</span>
            </div>
          </div>

          <div class="terms">
            <div class="section-title">Payment Terms</div>
            <p>${quotation.paymentTerms}</p>
            
            ${quotation.notes ? `
              <div class="section-title">Notes</div>
              <p>${quotation.notes}</p>
            ` : ''}
            
            ${quotation.termsAndConditions ? `
              <div class="section-title">Terms & Conditions</div>
              <p>${quotation.termsAndConditions}</p>
            ` : ''}
          </div>

          <div class="footer">
            <p>Thank you for your business!</p>
          </div>
        </body>
        </html>
      `;

      const { uri } = await Print.printToFileAsync({
        html: htmlContent,
        base64: false,
      });

      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: `Share Quotation ${quotation.quotationNumber}`,
        });
      } else {
        Alert.alert('Success', 'PDF generated successfully!');
      }

    } catch (error) {
      console.error('Generate PDF error:', error);
      Alert.alert('Error', 'Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  }, []);

  // Reset quotation form
  const resetQuotationForm = useCallback(() => {
    setQuotationForm({
      clientId: client?.id || '',
      clientName: client?.name || '',
      clientEmail: client?.email || '',
      clientPhone: client?.phone || '',
      clientAddress: client?.address || '',
      quotationNumber: '',
      title: '',
      description: '',
      validUntil: '',
      items: [],
      subtotal: 0,
      taxRate: 20,
      taxAmount: 0,
      discountType: 'percentage',
      discountValue: 0,
      discountAmount: 0,
      totalAmount: 0,
      currency: 'GBP',
      paymentTerms: '30 days',
      notes: '',
      termsAndConditions: '',
      companyInfo: {
        name: 'Your Company Name',
        address: 'Your Company Address',
        phone: 'Your Phone Number',
        email: 'your@email.com',
        website: 'www.yourwebsite.com',
        logo: '',
      },
    });
    setSelectedQuotation(null);
  }, [client]);

  // Get filtered quotations
  const getFilteredQuotations = useCallback(() => {
    switch (activeTab) {
      case 'active':
        return quotations.filter(quotation => 
          ['sent', 'viewed'].includes(quotation.status) && 
          new Date(quotation.validUntil) > new Date()
        );
      case 'draft':
        return draftQuotations;
      case 'sent':
        return quotations.filter(quotation => quotation.status === 'sent');
      case 'accepted':
        return quotations.filter(quotation => quotation.status === 'accepted');
      case 'expired':
        return quotations.filter(quotation => 
          new Date(quotation.validUntil) <= new Date()
        );
      default:
        return quotations;
    }
  }, [quotations, draftQuotations, activeTab]);

  // Format currency
  const formatCurrency = useCallback((amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // Get status color
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'draft':
        return '#64748B';
      case 'sent':
        return '#4A90E2';
      case 'viewed':
        return '#F59E0B';
      case 'accepted':
        return '#10B981';
      case 'rejected':
        return '#EF4444';
      case 'expired':
        return '#94A3B8';
      default:
        return '#64748B';
    }
  }, []);

  // Get status text
  const getStatusText = useCallback((status) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'sent':
        return 'Sent';
      case 'viewed':
        return 'Viewed';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'expired':
        return 'Expired';
      default:
        return 'Unknown';
    }
  }, []);

  // Update calculations when form changes
  useEffect(() => {
    if (quotationForm.items.length > 0) {
      updateItemCalculations();
    }
  }, [quotationForm.items, quotationForm.taxRate, quotationForm.discountType, quotationForm.discountValue]);

  // Render quotation item
  const renderQuotationItem = useCallback(({ item }) => {
    const statusColor = getStatusColor(item.status);
    const statusText = getStatusText(item.status);
    const isExpired = new Date(item.validUntil) <= new Date();
    
    return (
      <TouchableOpacity
        style={styles.quotationCard}
        onPress={() => {
          setSelectedQuotation(item);
          setShowQuotationDetailsModal(true);
        }}
      >
        <View style={styles.quotationHeader}>
          <View style={styles.quotationInfo}>
            <Text style={styles.quotationNumber}>{item.quotationNumber}</Text>
            <Text style={styles.quotationTitle} numberOfLines={1}>{item.title}</Text>
            <Text style={styles.quotationClient}>{item.clientName}</Text>
          </View>
          
          <View style={[styles.statusBadge, { backgroundColor: isExpired ? '#94A3B8' : statusColor }]}>
            <Text style={styles.statusBadgeText}>{isExpired ? 'EXPIRED' : statusText}</Text>
          </View>
        </View>
        
        <View style={styles.quotationDetails}>
          <View style={styles.quotationAmount}>
            <Text style={styles.quotationAmountLabel}>Total Amount</Text>
            <Text style={styles.quotationAmountValue}>
              {formatCurrency(item.totalAmount, item.currency)}
            </Text>
          </View>
          
          <View style={styles.quotationMeta}>
            <View style={styles.quotationMetaItem}>
              <MaterialCommunityIcons name="calendar" size={16} color="#64748B" />
              <Text style={styles.quotationMetaText}>
                Created: {formatDate(item.createdAt)}
              </Text>
            </View>
            
            <View style={styles.quotationMetaItem}>
              <MaterialCommunityIcons name="calendar-clock" size={16} color="#64748B" />
              <Text style={styles.quotationMetaText}>
                Valid until: {formatDate(item.validUntil)}
              </Text>
            </View>
            
            <View style={styles.quotationMetaItem}>
              <MaterialCommunityIcons name="format-list-numbered" size={16} color="#64748B" />
              <Text style={styles.quotationMetaText}>
                {item.items.length} item{item.items.length !== 1 ? 's' : ''}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.quotationFooter}>
          <View style={styles.quotationActions}>
            {item.status === 'draft' && (
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => {
                  setSelectedQuotation(item);
                  setQuotationForm(item);
                  setShowQuotationModal(true);
                }}
              >
                <MaterialCommunityIcons name="pencil" size={16} color="#4A90E2" />
                <Text style={styles.editButtonText}>Edit</Text>
              </TouchableOpacity>
            )}
            
            <TouchableOpacity
              style={styles.pdfButton}
              onPress={() => generatePDF(item)}
              disabled={isGeneratingPDF}
            >
              <MaterialCommunityIcons name="file-pdf-box" size={16} color="#EF4444" />
              <Text style={styles.pdfButtonText}>PDF</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.shareButton}
              onPress={() => {
                Share.share({
                  message: `Quotation ${item.quotationNumber} for ${item.title} - Total: ${formatCurrency(item.totalAmount)}`,
                  title: `Quotation ${item.quotationNumber}`,
                });
              }}
            >
              <MaterialCommunityIcons name="share" size={16} color="#64748B" />
              <Text style={styles.shareButtonText}>Share</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [getStatusColor, getStatusText, formatCurrency, formatDate, generatePDF, isGeneratingPDF]);

  const filteredQuotations = getFilteredQuotations();

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Quotations...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient
        colors={['#4A90E2', '#357ABD']}
        style={styles.header}
      >
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>Quotations</Text>
        
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => {
            resetQuotationForm();
            setShowQuotationModal(true);
          }}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[
            { key: 'active', label: 'Active', icon: 'clock-outline', count: quotations.filter(q => ['sent', 'viewed'].includes(q.status) && new Date(q.validUntil) > new Date()).length },
            { key: 'draft', label: 'Drafts', icon: 'file-document-edit-outline', count: draftQuotations.length },
            { key: 'sent', label: 'Sent', icon: 'send', count: quotations.filter(q => q.status === 'sent').length },
            { key: 'accepted', label: 'Accepted', icon: 'check-circle', count: quotations.filter(q => q.status === 'accepted').length },
            { key: 'expired', label: 'Expired', icon: 'clock-alert-outline', count: quotations.filter(q => new Date(q.validUntil) <= new Date()).length },
          ].map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.activeTab]}
              onPress={() => setActiveTab(tab.key)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={20}
                color={activeTab === tab.key ? '#4A90E2' : '#64748B'}
              />
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.activeTabText,
                ]}
              >
                {tab.label}
              </Text>
              {tab.count > 0 && (
                <View style={styles.tabBadge}>
                  <Text style={styles.tabBadgeText}>{tab.count}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <FlatList
          data={filteredQuotations}
          renderItem={renderQuotationItem}
          keyExtractor={(item) => item.id}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.listContainer}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={['#4A90E2']}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="file-document-outline" size={64} color="#94A3B8" />
              <Text style={styles.emptyStateTitle}>No Quotations Found</Text>
              <Text style={styles.emptyStateDescription}>
                <Text>{activeTab === 'active' && 'No active quotations at the moment'}</Text>
                <Text>{activeTab === 'draft' && 'No draft quotations saved'}</Text>
                <Text>{activeTab === 'sent' && 'No sent quotations yet'}</Text>
                <Text>{activeTab === 'accepted' && 'No accepted quotations yet'}</Text>
                <Text>{activeTab === 'expired' && 'No expired quotations yet'}</Text>
              </Text>
              
              <TouchableOpacity
                style={styles.createQuotationButton}
                onPress={() => {
                  resetQuotationForm();
                  setShowQuotationModal(true);
                }}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
                <Text style={styles.createQuotationButtonText}>Create New Quotation</Text>
              </TouchableOpacity>
            </View>
          }
        />
      </View>

      {/* Quotation Form Modal */}
      <Modal
        visible={showQuotationModal}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={() => setShowQuotationModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <KeyboardAvoidingView 
            style={styles.modalContainer}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          >
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowQuotationModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
              
              <Text style={styles.modalTitle}>
                <Text>{selectedQuotation && selectedQuotation.status === 'draft' ? 'Edit Quotation' : 'Create New Quotation'}</Text>
              </Text>
              
              <TouchableOpacity onPress={() => setShowTemplateModal(true)}>
                <MaterialCommunityIcons name="file-document-multiple" size={24} color="#4A90E2" />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {/* Client Information */}
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Client Information</Text>
                
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Client Name *</Text>
                  <TextInput
                    style={styles.formInput}
                    value={quotationForm.clientName}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, clientName: text }))}
                    placeholder="Enter client name"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Client Email *</Text>
                  <TextInput
                    style={styles.formInput}
                    value={quotationForm.clientEmail}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, clientEmail: text }))}
                    placeholder="Enter client email"
                    keyboardType="email-address"
                  />
                </View>

                <View style={styles.formRow}>
                  <View style={[styles.formGroup, { flex: 1 }]}>
                    <Text style={styles.formLabel}>Phone</Text>
                    <TextInput
                      style={styles.formInput}
                      value={quotationForm.clientPhone}
                      onChangeText={(text) => setQuotationForm(prev => ({ ...prev, clientPhone: text }))}
                      placeholder="Phone number"
                      keyboardType="phone-pad"
                    />
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Address</Text>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={quotationForm.clientAddress}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, clientAddress: text }))}
                    placeholder="Client address"
                    multiline
                    numberOfLines={3}
                  />
                </View>
              </View>

              {/* Quotation Details */}
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Quotation Details</Text>
                
                <View style={styles.formRow}>
                  <View style={[styles.formGroup, { flex: 2 }]}>
                    <Text style={styles.formLabel}>Title *</Text>
                    <TextInput
                      style={styles.formInput}
                      value={quotationForm.title}
                      onChangeText={(text) => setQuotationForm(prev => ({ ...prev, title: text }))}
                      placeholder="Quotation title"
                    />
                  </View>
                  
                  <View style={[styles.formGroup, { flex: 1, marginLeft: 12 }]}>
                    <Text style={styles.formLabel}>Valid Until *</Text>
                    <TextInput
                      style={styles.formInput}
                      value={quotationForm.validUntil}
                      onChangeText={(text) => setQuotationForm(prev => ({ ...prev, validUntil: text }))}
                      placeholder="YYYY-MM-DD"
                    />
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Description</Text>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={quotationForm.description}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, description: text }))}
                    placeholder="Quotation description"
                    multiline
                    numberOfLines={3}
                  />
                </View>
              </View>

              {/* Items */}
              <View style={styles.formSection}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.formSectionTitle}>Items</Text>
                  <TouchableOpacity
                    style={styles.addButton}
                    onPress={addItem}
                  >
                    <MaterialCommunityIcons name="plus" size={20} color="#4A90E2" />
                  </TouchableOpacity>
                </View>
                
                {quotationForm.items.map((item, index) => (
                  <View key={item.id} style={styles.itemContainer}>
                    <View style={styles.itemHeader}>
                      <Text style={styles.itemTitle}>Item {index + 1}</Text>
                      <TouchableOpacity
                        style={styles.removeButton}
                        onPress={() => removeItem(item.id)}
                      >
                        <MaterialCommunityIcons name="close" size={20} color="#EF4444" />
                      </TouchableOpacity>
                    </View>
                    
                    <TextInput
                      style={styles.formInput}
                      value={item.description}
                      onChangeText={(text) => updateItem(item.id, 'description', text)}
                      placeholder="Item description"
                    />
                    
                    <View style={styles.itemRow}>
                      <View style={[styles.formGroup, { flex: 1 }]}>
                        <Text style={styles.formLabel}>Qty</Text>
                        <TextInput
                          style={styles.formInput}
                          value={item.quantity.toString()}
                          onChangeText={(text) => updateItem(item.id, 'quantity', parseFloat(text) || 0)}
                          placeholder="0"
                          keyboardType="numeric"
                        />
                      </View>
                      
                      <View style={[styles.formGroup, { flex: 1, marginLeft: 8 }]}>
                        <Text style={styles.formLabel}>Unit</Text>
                        <TextInput
                          style={styles.formInput}
                          value={item.unit}
                          onChangeText={(text) => updateItem(item.id, 'unit', text)}
                          placeholder="pcs"
                        />
                      </View>
                      
                      <View style={[styles.formGroup, { flex: 1, marginLeft: 8 }]}>
                        <Text style={styles.formLabel}>Unit Price</Text>
                        <TextInput
                          style={styles.formInput}
                          value={item.unitPrice.toString()}
                          onChangeText={(text) => updateItem(item.id, 'unitPrice', parseFloat(text) || 0)}
                          placeholder="0.00"
                          keyboardType="numeric"
                        />
                      </View>
                      
                      <View style={[styles.formGroup, { flex: 1, marginLeft: 8 }]}>
                        <Text style={styles.formLabel}>Total</Text>
                        <View style={styles.totalDisplay}>
                          <Text style={styles.totalText}>{formatCurrency(item.total)}</Text>
                        </View>
                      </View>
                    </View>
                  </View>
                ))}
              </View>

              {/* Totals */}
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Totals</Text>
                
                <View style={styles.totalsContainer}>
                  <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Subtotal:</Text>
                    <Text style={styles.totalValue}>{formatCurrency(quotationForm.subtotal)}</Text>
                  </View>
                  
                  <View style={styles.discountRow}>
                    <View style={styles.discountControls}>
                      <Text style={styles.totalLabel}>Discount:</Text>
                      <View style={styles.discountInputs}>
                        <TextInput
                          style={[styles.formInput, styles.discountInput]}
                          value={quotationForm.discountValue.toString()}
                          onChangeText={(text) => setQuotationForm(prev => ({ ...prev, discountValue: parseFloat(text) || 0 }))}
                          placeholder="0"
                          keyboardType="numeric"
                        />
                        <TouchableOpacity
                          style={[styles.discountTypeButton, quotationForm.discountType === 'percentage' && styles.discountTypeButtonActive]}
                          onPress={() => setQuotationForm(prev => ({ ...prev, discountType: 'percentage' }))}
                        >
                          <Text style={[styles.discountTypeText, quotationForm.discountType === 'percentage' && styles.discountTypeTextActive]}>%</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.discountTypeButton, quotationForm.discountType === 'fixed' && styles.discountTypeButtonActive]}
                          onPress={() => setQuotationForm(prev => ({ ...prev, discountType: 'fixed' }))}
                        >
                          <Text style={[styles.discountTypeText, quotationForm.discountType === 'fixed' && styles.discountTypeTextActive]}>£</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                    <Text style={styles.totalValue}>-{formatCurrency(quotationForm.discountAmount)}</Text>
                  </View>
                  
                  <View style={styles.taxRow}>
                    <View style={styles.taxControls}>
                      <Text style={styles.totalLabel}>VAT:</Text>
                      <TextInput
                        style={[styles.formInput, styles.taxInput]}
                        value={quotationForm.taxRate.toString()}
                        onChangeText={(text) => setQuotationForm(prev => ({ ...prev, taxRate: parseFloat(text) || 0 }))}
                        placeholder="20"
                        keyboardType="numeric"
                      />
                      <Text style={styles.taxPercent}>%</Text>
                    </View>
                    <Text style={styles.totalValue}>{formatCurrency(quotationForm.taxAmount)}</Text>
                  </View>
                  
                  <View style={styles.finalTotalRow}>
                    <Text style={styles.finalTotalLabel}>Total:</Text>
                    <Text style={styles.finalTotalValue}>{formatCurrency(quotationForm.totalAmount)}</Text>
                  </View>
                </View>
              </View>

              {/* Terms & Notes */}
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Terms & Notes</Text>
                
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Payment Terms</Text>
                  <TextInput
                    style={styles.formInput}
                    value={quotationForm.paymentTerms}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, paymentTerms: text }))}
                    placeholder="e.g., 30 days"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Notes</Text>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={quotationForm.notes}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, notes: text }))}
                    placeholder="Additional notes..."
                    multiline
                    numberOfLines={4}
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>Terms & Conditions</Text>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={quotationForm.termsAndConditions}
                    onChangeText={(text) => setQuotationForm(prev => ({ ...prev, termsAndConditions: text }))}
                    placeholder="Terms and conditions..."
                    multiline
                    numberOfLines={4}
                  />
                </View>
              </View>

              {/* Action Buttons */}
              <View style={styles.formActions}>
                <TouchableOpacity
                  style={[styles.actionButton, styles.draftButton]}
                  onPress={saveDraftQuotation}
                  disabled={isSubmitting}
                >
                  <MaterialCommunityIcons name="content-save" size={20} color="#64748B" />
                  <Text style={[styles.actionButtonText, { color: '#64748B' }]}>Save Draft</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.actionButton, styles.sendButton]}
                  onPress={sendQuotation}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <ActivityIndicator size={20} color="#FFFFFF" />
                  ) : (
                    <MaterialCommunityIcons name="send" size={20} color="#FFFFFF" />
                  )}
                  <Text style={styles.actionButtonText}>
                    <Text>{isSubmitting ? 'Sending...' : 'Send Quotation'}</Text>
                  </Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* Template Modal */}
      <Modal
        visible={showTemplateModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowTemplateModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Choose Template</Text>
            <TouchableOpacity onPress={() => setShowTemplateModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {templates.map((template) => (
              <TouchableOpacity
                key={template.id}
                style={styles.templateItem}
                onPress={() => applyTemplate(template)}
              >
                <View style={styles.templateInfo}>
                  <Text style={styles.templateName}>{template.name}</Text>
                  <Text style={styles.templateDescription}>
                    {template.items.length} items • {template.paymentTerms}
                  </Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={24} color="#64748B" />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Quotation Details Modal */}
      <Modal
        visible={showQuotationDetailsModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowQuotationDetailsModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Quotation Details</Text>
            <TouchableOpacity onPress={() => setShowQuotationDetailsModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          {selectedQuotation && (
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {/* Quotation Header */}
              <View style={styles.detailsHeader}>
                <Text style={styles.detailsNumber}>{selectedQuotation.quotationNumber}</Text>
                <Text style={styles.detailsTitle}>{selectedQuotation.title}</Text>
                <Text style={styles.detailsClient}>{selectedQuotation.clientName}</Text>
                
                <View style={styles.detailsStatus}>
                  <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedQuotation.status) }]}>
                    <Text style={styles.statusBadgeText}>{getStatusText(selectedQuotation.status)}</Text>
                  </View>
                </View>
              </View>

              {/* Total Amount */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Total Amount</Text>
                <Text style={styles.totalAmountLarge}>
                  {formatCurrency(selectedQuotation.totalAmount, selectedQuotation.currency)}
                </Text>
              </View>

              {/* Items */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Items</Text>
                {selectedQuotation.items.map((item, index) => (
                  <View key={item.id || index} style={styles.itemDetailRow}>
                    <View style={styles.itemDetailInfo}>
                      <Text style={styles.itemDetailDescription}>{item.description}</Text>
                      <Text style={styles.itemDetailMeta}>
                        {item.quantity} {item.unit} × {formatCurrency(item.unitPrice)}
                      </Text>
                    </View>
                    <Text style={styles.itemDetailTotal}>{formatCurrency(item.total)}</Text>
                  </View>
                ))}
              </View>

              {/* Breakdown */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Breakdown</Text>
                <View style={styles.breakdownContainer}>
                  <View style={styles.breakdownRow}>
                    <Text style={styles.breakdownLabel}>Subtotal</Text>
                    <Text style={styles.breakdownValue}>{formatCurrency(selectedQuotation.subtotal)}</Text>
                  </View>
                  
                  {selectedQuotation.discountAmount > 0 && (
                    <View style={styles.breakdownRow}>
                      <Text style={styles.breakdownLabel}>
                        Discount ({selectedQuotation.discountType === 'percentage' ? selectedQuotation.discountValue + '%' : formatCurrency(selectedQuotation.discountValue)})
                      </Text>
                      <Text style={styles.breakdownValue}>-{formatCurrency(selectedQuotation.discountAmount)}</Text>
                    </View>
                  )}
                  
                  <View style={styles.breakdownRow}>
                    <Text style={styles.breakdownLabel}>VAT ({selectedQuotation.taxRate}%)</Text>
                    <Text style={styles.breakdownValue}>{formatCurrency(selectedQuotation.taxAmount)}</Text>
                  </View>
                  
                  <View style={[styles.breakdownRow, styles.breakdownTotal]}>
                    <Text style={styles.breakdownTotalLabel}>Total</Text>
                    <Text style={styles.breakdownTotalValue}>{formatCurrency(selectedQuotation.totalAmount)}</Text>
                  </View>
                </View>
              </View>

              {/* Terms & Notes */}
              {(selectedQuotation.paymentTerms || selectedQuotation.notes) && (
                <View style={styles.detailsSection}>
                  <Text style={styles.detailsSectionTitle}>Terms & Notes</Text>
                  
                  {selectedQuotation.paymentTerms && (
                    <View style={styles.termsItem}>
                      <Text style={styles.termsLabel}>Payment Terms:</Text>
                      <Text style={styles.termsValue}>{selectedQuotation.paymentTerms}</Text>
                    </View>
                  )}
                  
                  {selectedQuotation.notes && (
                    <View style={styles.termsItem}>
                      <Text style={styles.termsLabel}>Notes:</Text>
                      <Text style={styles.termsValue}>{selectedQuotation.notes}</Text>
                    </View>
                  )}
                </View>
              )}

              {/* Timestamps */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Timeline</Text>
                <View style={styles.timestampItem}>
                  <MaterialCommunityIcons name="calendar-plus" size={16} color="#64748B" />
                  <Text style={styles.timestampText}>
                    Created: {formatDate(selectedQuotation.createdAt)}
                  </Text>
                </View>
                
                {selectedQuotation.sentAt && (
                  <View style={styles.timestampItem}>
                    <MaterialCommunityIcons name="send" size={16} color="#64748B" />
                    <Text style={styles.timestampText}>
                      Sent: {formatDate(selectedQuotation.sentAt)}
                    </Text>
                  </View>
                )}
                
                <View style={styles.timestampItem}>
                  <MaterialCommunityIcons name="calendar-clock" size={16} color="#64748B" />
                  <Text style={styles.timestampText}>
                    Valid until: {formatDate(selectedQuotation.validUntil)}
                  </Text>
                </View>
              </View>

              {/* Action Buttons */}
              <View style={styles.detailsActions}>
                <TouchableOpacity
                  style={[styles.actionButton, styles.pdfActionButton]}
                  onPress={() => generatePDF(selectedQuotation)}
                  disabled={isGeneratingPDF}
                >
                  {isGeneratingPDF ? (
                    <ActivityIndicator size={20} color="#EF4444" />
                  ) : (
                    <MaterialCommunityIcons name="file-pdf-box" size={20} color="#EF4444" />
                  )}
                  <Text style={[styles.actionButtonText, { color: '#EF4444' }]}>
                    <Text>{isGeneratingPDF ? 'Generating...' : 'Generate PDF'}</Text>
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.actionButton, styles.shareActionButton]}
                  onPress={() => {
                    Share.share({
                      message: `Quotation ${selectedQuotation.quotationNumber} for ${selectedQuotation.title} - Total: ${formatCurrency(selectedQuotation.totalAmount)}`,
                      title: `Quotation ${selectedQuotation.quotationNumber}`,
                    });
                  }}
                >
                  <MaterialCommunityIcons name="share" size={20} color="#4A90E2" />
                  <Text style={[styles.actionButtonText, { color: '#4A90E2' }]}>Share</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          )}
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  addButton: {
    padding: 8,
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginRight: 8,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#4A90E2',
  },
  tabBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
  },
  listContainer: {
    padding: 20,
  },
  quotationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  quotationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  quotationInfo: {
    flex: 1,
    marginRight: 12,
  },
  quotationNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4A90E2',
    marginBottom: 2,
  },
  quotationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    lineHeight: 22,
  },
  quotationClient: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  quotationDetails: {
    marginBottom: 16,
  },
  quotationAmount: {
    marginBottom: 12,
  },
  quotationAmountLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  quotationAmountValue: {
    fontSize: 20,
    fontWeight: '600',
    color: '#10B981',
  },
  quotationMeta: {
    gap: 8,
  },
  quotationMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quotationMetaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 6,
  },
  quotationFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quotationActions: {
    flexDirection: 'row',
    gap: 12,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#F0F7FF',
    borderRadius: 6,
  },
  editButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#4A90E2',
    marginLeft: 4,
  },
  pdfButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#FEF2F2',
    borderRadius: 6,
  },
  pdfButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#EF4444',
    marginLeft: 4,
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#F1F5F9',
    borderRadius: 6,
  },
  shareButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 24,
  },
  createQuotationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#4A90E2',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  createQuotationButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalContent: {
    padding: 20,
  },
  formSection: {
    marginBottom: 24,
  },
  formSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  formGroup: {
    marginBottom: 16,
  },
  formRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  itemContainer: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  removeButton: {
    padding: 4,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 12,
  },
  totalDisplay: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
  },
  totalsContainer: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 8,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  totalLabel: {
    fontSize: 14,
    color: '#374151',
  },
  totalValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0F172A',
  },
  discountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  discountControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  discountInputs: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  discountInput: {
    width: 60,
    height: 36,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    marginRight: 8,
  },
  discountTypeButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    marginRight: 4,
    backgroundColor: '#FFFFFF',
  },
  discountTypeButtonActive: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  discountTypeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
  },
  discountTypeTextActive: {
    color: '#FFFFFF',
  },
  taxRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  taxControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  taxInput: {
    width: 60,
    height: 36,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    marginLeft: 12,
    marginRight: 4,
  },
  taxPercent: {
    fontSize: 14,
    color: '#64748B',
  },
  finalTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    marginTop: 8,
  },
  finalTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  finalTotalValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#10B981',
  },
  formActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
    marginBottom: 32,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  draftButton: {
    backgroundColor: '#F8FAFC',
    borderColor: '#D1D5DB',
  },
  sendButton: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  templateItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  templateInfo: {
    flex: 1,
  },
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  templateDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  detailsHeader: {
    marginBottom: 24,
  },
  detailsNumber: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4A90E2',
    marginBottom: 4,
  },
  detailsTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#0F172A',
    lineHeight: 32,
    marginBottom: 8,
  },
  detailsClient: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 16,
  },
  detailsStatus: {
    alignItems: 'flex-start',
  },
  detailsSection: {
    marginBottom: 24,
  },
  detailsSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  totalAmountLarge: {
    fontSize: 32,
    fontWeight: '600',
    color: '#10B981',
  },
  itemDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  itemDetailInfo: {
    flex: 1,
    marginRight: 16,
  },
  itemDetailDescription: {
    fontSize: 16,
    fontWeight: '500',
    color: '#0F172A',
    marginBottom: 4,
  },
  itemDetailMeta: {
    fontSize: 14,
    color: '#64748B',
  },
  itemDetailTotal: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  breakdownContainer: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 8,
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  breakdownLabel: {
    fontSize: 14,
    color: '#374151',
  },
  breakdownValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0F172A',
  },
  breakdownTotal: {
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    marginTop: 8,
    paddingTop: 12,
  },
  breakdownTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  breakdownTotalValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#10B981',
  },
  termsItem: {
    marginBottom: 16,
  },
  termsLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  termsValue: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  timestampItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  timestampText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
  },
  detailsActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  pdfActionButton: {
    backgroundColor: '#FEF2F2',
    borderColor: '#EF4444',
  },
  shareActionButton: {
    backgroundColor: '#F0F7FF',
    borderColor: '#4A90E2',
  },
});
